import * as jsts from "jsts";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Curve3 } from "@babylonjs/core/Maths/math.path";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { CreateLines, CreateLineSystem } from "@babylonjs/core/Meshes/Builders/linesBuilder";
import { CreateRibbon } from "@babylonjs/core/Meshes/Builders/ribbonBuilder";
import { ExtrudeShape } from "@babylonjs/core/Meshes/Builders/shapeBuilder";
import { Vector2, Vector3 } from "@babylonjs/core/Maths/math.vector";
import { VertexBuffer } from "@babylonjs/core/Buffers/buffer";
import { ElementGeometry } from "./ElementGeometry";
import { RoomManager } from './../../RoomManager';
import { Util } from "../../utils/Util";
import { ShadowGenerator } from "@egr/wcf/modules/core/rendering/ShadowGenerator";
import { newError } from '@egr/wcf/modules/utils/error';
export class Spline extends ElementGeometry {
    constructor(pRoomManager, pElement) {
        super(pRoomManager, pElement);
        this.mRoomElement = pElement;
        this.mRoomManager = pRoomManager;
        this.mPoints = new Array();
        this.mMidPoints = new Array();
        this.mCutPoints = new Array();
        this.mLinePoints = new Array();
        this.mWidth = 0;
        this.mDepth = 0;
        this.mMaterial = -1;
        this.mColor = "#999999";
        this.mCenter = Vector3.Zero();
        this.mminPoint = Vector3.Zero();
        this.mmaxPoint = Vector3.Zero();
        this.mArea = 0;
        this.mPerimeter = 0;
        this.mClosedPolygon = false;
        this.mCatMullRom = new Curve3([]);
        this.mSplinePrecision = 20;
        this.mHillPoints_Offset = null;
        this.mHillPoints_Base = null;
        this.mcanCreateHill = false;
        this.mRoomElement.Params.ElementPoints = new Array();
        this.mRoomElement.Params.ElementOriginalPoints = new Array();
        this.mRoomElement.Params.material = -1;
        this.mRoomElement.Params.materialColor = this.mColor;
    }
    getType() {
        if (this.mRoomElement.Params.terrainType === "Flat") {
            return ("Spline");
        }
        return ("Spline");
    }
    setMesh(pMesh) {
        this.mArea = Util.calcMeshArea(pMesh) * 0.25;
        this.mPerimeter = this.mRoomManager.calcMeshPerimeter(pMesh);
    }
    get drawnMesh() {
        return (this.mMesh2d);
    }
    getPoints() {
        return (this.mPoints);
    }
    getMidPoints() {
        this.createCatMullRom();
        this.mMidPoints = [];
        let tcmPoints = this.mCatMullRom.getPoints();
        let tlastIdx = (tcmPoints.length - 1) - (this.mSplinePrecision * 0.5);
        this.mMidPoints.push(tcmPoints[tlastIdx]);
        for (let i = this.mSplinePrecision * 0.5; i < tcmPoints.length - (this.mSplinePrecision * 0.5 + 1); i += this.mSplinePrecision) {
            this.mMidPoints.push(tcmPoints[i]);
        }
        return (this.mMidPoints);
    }
    getPolygon(pOffset = true) {
        let tpolygonPoints = [];
        let tsplinePoints = Util.to3dArray(this.get2dtcatmullRomPoints(this.mCatMullRom));
        for (let tP of tsplinePoints) {
            let tnodePos = new Vector3(0, 0, 0);
            if (pOffset)
                tnodePos = this.mRoomElement.position.clone();
            let tnP = tP.add(tnodePos);
            tpolygonPoints.push(tnP);
        }
        return (tpolygonPoints);
    }
    getPolygonCenter(pOffset = true) {
        let tpolygonPoints = [];
        let tsplinePoints = Util.to3dArray(this.get2dtcatmullRomPoints(this.mCatMullRom));
        for (let tP of tsplinePoints) {
            let tnodePos = new Vector3(0, 0, 0);
            if (pOffset)
                tnodePos = this.mRoomElement.position.clone();
            let tnP = tP.add(tnodePos);
            tpolygonPoints.push(tnP);
        }
        let tminX = tpolygonPoints[0].x;
        let tminY = tpolygonPoints[0].z;
        let tmaxX = tpolygonPoints[0].x;
        let tmaxY = tpolygonPoints[0].z;
        for (let tP of tpolygonPoints) {
            tminX = Math.min(tP.x, tminX);
            tmaxX = Math.max(tP.x, tmaxX);
            tminY = Math.min(tP.z, tminY);
            tmaxY = Math.max(tP.z, tmaxY);
        }
        let tP1 = new Vector2(tminX, tminY);
        let tP2 = new Vector2(tmaxX, tmaxY);
        let tRes = Vector2.Center(tP1, tP2);
        return (tRes);
    }
    getLinePoints() {
        return this.mLinePoints;
    }
    width() {
        return this.mWidth;
    }
    depth() {
        return this.mDepth;
    }
    material() {
        return this.mMaterial;
    }
    center() {
        return this.mCenter;
    }
    minPoint() {
        return this.mminPoint;
    }
    maxPoint() {
        return this.mmaxPoint;
    }
    showInteractors(pShow) {
        if (this.mInteractors != null)
            this.mInteractors.showInteractors(pShow);
    }
    correctDirection(pCreateNew) {
        if (pCreateNew)
            return;
        let tPoints = new Array();
        for (let tP of this.mPoints) {
            tPoints.push(tP);
        }
        if (!Util.vectorEquals3(tPoints[0], tPoints[tPoints.length - 1])) {
            tPoints.push(tPoints[0]);
        }
        let tClockwise = Util._clockWise3(tPoints);
        if (tClockwise && !pCreateNew) {
            this.mPoints.push(this.mPoints[0]);
            this.mPoints.reverse();
            this.mPoints.pop();
        }
    }
    correctDirection1() {
        let tClockwise = Util._clockWise3(this.mCutPoints);
        if (tClockwise)
            this.mCutPoints.reverse();
    }
    getInitialParams() {
        let tParams = {};
        tParams.width = 0.5;
        tParams.height = 0;
        tParams.floorOffset = 0.0;
        tParams.color = this.mColor;
        return (tParams);
    }
    getparent() {
        return (this.mParent);
    }
    get Area() {
        return (this.mArea);
    }
    get Perimeter() {
        return (this.mPerimeter);
    }
    create2dGeometry(pParent) {
        this.mParent = pParent;
        let tSpline = this.createSpline(pParent);
        let tHill = this.createHill(pParent);
        if (tSpline != null) {
            this.mRoomElement.setMesh2d(tSpline);
        }
        this.mergeShapeMeshes(pParent, this.mRoomElement.setMesh2d);
    }
    create3dGeometry(pParent) {
        let tSpline = this.createSpline(pParent);
        let tHill = this.createHill(pParent);
        if (tSpline != null) {
            this.mRoomElement.setMesh3d(tSpline);
        }
        this.mergeShapeMeshes(pParent, this.mRoomElement.setMesh3d);
    }
    createSpecialGeometry(pParent) {
        if (this.mRoomElement.crossHairVisible)
            this.createCrossHair(pParent);
    }
    createAdditionalGeometry(pParent) {
        this.createHelpLines(pParent);
    }
    getProperties() {
        let tProperties = new Array();
        return (tProperties);
    }
    serializeIOData() {
        return (this.mRoomElement.Params);
    }
    loadIOData(pData) {
        let tParams = pData;
        let tPoints = new Array();
        for (let tEP of tParams.ElementPoints) {
            let tP = new Vector3(0, 0, 0);
            tP.x = tEP.x;
            tP.z = tEP.z;
            tPoints.push(tP);
        }
        this.setPoints(tPoints);
    }
    setPoints(pPoints, pCreateNew = false) {
        this.mPoints = [];
        for (let tPoint of pPoints) {
            let tP = new Vector3(0, 0, 0);
            tP.x = tPoint.x;
            tP.z = tPoint.z;
            this.mPoints.push(tP);
        }
        if (this.mPoints.length > 2) {
            this.mClosedPolygon = true;
            this.mRoomElement.setClosedPolygon(this.mClosedPolygon);
        }
        this.correctDirection(pCreateNew);
        if (this.mPoints.length > 3 && Util.vectorEquals3(this.mPoints[0], this.mPoints[this.mPoints.length - 1]))
            this.mPoints.pop();
        this.mRoomElement.Params.ElementPoints = this.mPoints;
        this.calcCenter();
        this.calcSize();
        this.createCatMullRom();
    }
    deletePoint(pIndex) {
        if (pIndex > this.mPoints.length - 1 || pIndex < 0 || this.mPoints.length === 0 || this.mPoints.length <= 3)
            return;
        this.mPoints.splice(pIndex, 1);
        this.mRoomElement.Params.ElementPoints = this.mPoints;
        this.createCatMullRom();
        this.calcCenter();
        this.calcSize();
    }
    addPoint(pIndex) {
        let tc1 = pIndex - 1;
        let tc2 = pIndex === this.mPoints.length ? 0 : pIndex;
        let tnewPoint = Vector3.Center(this.mPoints[tc1], this.mPoints[tc2]);
        this.mPoints.splice(pIndex, 0, tnewPoint);
        this.mRoomElement.Params.ElementPoints = this.mPoints;
        this.createCatMullRom();
        this.calcCenter();
        this.calcSize();
    }
    calcCenter() {
        let t2dPoints = new Array();
        for (let tP of this.mPoints)
            t2dPoints.push(new Vector2(tP.x, tP.z));
        let tCenter = new Vector3(0, 0, 0);
        if (this.mPoints.length > 2)
            tCenter = Util.getPolygonCentroid(t2dPoints);
        else {
            tCenter = Vector3.Center(this.mPoints[0], this.mPoints[1]);
        }
        this.mCenter = tCenter;
    }
    createCatMullRom() {
        if (this.mClosedPolygon) {
            let tPoints = new Array();
            for (let tP of this.mRoomElement.Params.ElementPoints) {
                tPoints.push(tP);
            }
            if ((tPoints.length > 3) &&
                (Util.vectorEquals3(tPoints[tPoints.length - 1], tPoints[tPoints.length - 2]))) {
                tPoints.pop();
            }
            this.mCatMullRom = Curve3.CreateCatmullRomSpline(tPoints, this.mSplinePrecision, true);
        }
        else {
            this.mCatMullRom = Curve3.CreateCatmullRomSpline(this.mRoomElement.Params.ElementPoints, this.mSplinePrecision, true);
        }
        this.calcSize();
        this.calcPerimeter();
    }
    createCatMullRom_Hill(pPoints) {
        let tRes = [];
        let tPoints = new Array();
        tPoints = pPoints.slice();
        tPoints = Util.correctPolygonDirection3(tPoints);
        if (tPoints.length > 3 && Util.vectorEquals3(tPoints[tPoints.length - 1], tPoints[0])) {
            tPoints.pop();
        }
        tRes = Curve3.CreateCatmullRomSpline(tPoints, this.mSplinePrecision, true).getPoints();
        if (tRes.length > 3 && Util.vectorEquals3(tRes[tRes.length - 1], tRes[0])) {
            tRes.pop();
        }
        tRes = Util.correctPolygonDirection3(tRes);
        return (tRes);
    }
    get2dtcatmullRomPoints(pcatMullRom) {
        let tRes = new Array();
        let tPoints = pcatMullRom.getPoints();
        for (let tP of tPoints) {
            tRes.push(new Vector2(tP.x, tP.z));
        }
        tRes.pop();
        return tRes;
    }
    calcPerimeter() {
        let tPoints = this.mCatMullRom.getPoints();
        let tDist = 0;
        for (let i = 1; i < tPoints.length; i++) {
            tDist += Vector3.Distance(tPoints[i - 1], tPoints[i]);
        }
        this.mPerimeter = tDist;
    }
    calcSize() {
        let tPoints = this.mCatMullRom.getPoints();
        if (tPoints.length === 0)
            return;
        let tminX = tPoints[0].x;
        let tminY = tPoints[0].z;
        let tmaxX = tPoints[0].x;
        let tmaxY = tPoints[0].z;
        for (let tPoint of tPoints) {
            tminX = Math.min(tPoint.x, tminX);
            tmaxX = Math.max(tPoint.x, tmaxX);
            tminY = Math.min(tPoint.z, tminY);
            tmaxY = Math.max(tPoint.z, tmaxY);
        }
        this.mWidth = tmaxX - tminX;
        this.mDepth = tmaxY - tminY;
        this.mRoomElement.setWidth(this.mWidth, false, false);
        this.mRoomElement.setDepth(this.mDepth, false, false);
        this.mminPoint = new Vector3(tminX, 0, tminY);
        this.mmaxPoint = new Vector3(tmaxX, 0, tmaxY);
    }
    drawOutline(pParent) {
        //for invisible picking plane
        let tpickShape = [
            new Vector3(-0.5, 0, 0.5),
            new Vector3(-0.5, 0, -0.5),
            new Vector3(0.5, 0, -0.5),
            new Vector3(0.5, 0, 0.5),
            new Vector3(-0.5, 0, 0.5)
        ];
        this.mLinePoints = this.mCatMullRom.getPoints();
        const tmeshName = RoomManager.ROOM_SHAPE2D_PREFIX + this.mRoomElement.type.toString();
        //if (this.mRoomElement.material == -1){
        let tcatmullRomSpline = CreateLines(tmeshName + RoomManager.ROOM_SHAPE2D_SUFFIXOUTLINE, { points: this.mCatMullRom.getPoints() }, this.mScene);
        tcatmullRomSpline.color = Color3.FromHexString("#FF0000");
        tcatmullRomSpline.parent = pParent;
        tcatmullRomSpline.isPickable = false;
        ShadowGenerator.disableMeshShadow(tcatmullRomSpline);
        tcatmullRomSpline.renderingGroupId = 1;
        this.mRoomElement.Params.MeshVertices = tcatmullRomSpline.getVerticesData(VertexBuffer.PositionKind);
        this.prepare3dMesh(tcatmullRomSpline);
        //}
        let tpolygon = ExtrudeShape("", { shape: tpickShape,
            path: Util.to3dArray(this.get2dtcatmullRomPoints(this.mCatMullRom)),
            sideOrientation: Mesh.DOUBLESIDE,
            updatable: true }, this.mScene);
        tpolygon.name = tmeshName + RoomManager.ROOM_SHAPE2D_SUFFIXPICKLINE;
        tpolygon.isPickable = true;
        tpolygon.parent = pParent;
        tpolygon.visibility = 0;
        ShadowGenerator.disableMeshShadow(tpolygon);
        this.prepare3dMesh(tpolygon);
        return (tpolygon);
    }
    getCorrectedPolyPath() {
        let tP = this.getPolygon(false);
        if (Util.vectorEquals3(tP[0], tP[tP.length - 1]))
            tP.pop();
        if ((tP.length > 3) && (Util.vectorEquals3(tP[tP.length - 1], tP[tP.length - 2]))) {
            tP.pop();
        }
        tP = Util.correctPolygonDirection3(tP);
        return (tP);
    }
    // tslint:disable-next-line:cyclomatic-complexity
    createOffsetCSG(pBasePoly) {
        let tRes = {};
        tRes.CAG = null;
        tRes.CSG = null;
        const tHillOffset = this.mRoomElement.Params.hillOffset;
        const tHillType = this.mRoomElement.Params.terrainType;
        if (tHillType === "Flat") {
            return (tRes);
        }
        let tbasePolygon = Util.correctPolygonDirection3(this.mPoints);
        let toffsetPoints = offset(tbasePolygon, (tHillType === 'Top' ? tHillOffset : -tHillOffset));
        if (toffsetPoints.length !== tbasePolygon.length) {
            return (tRes);
        }
        // offset plane
        let tPointsOffset = new Array();
        let tCAGOffset = null;
        toffsetPoints = this.createCatMullRom_Hill(toffsetPoints);
        this.limitOffsetDistance();
        if (tHillType === 'Bottom') {
            for (let tP1 of toffsetPoints) {
                tP1.addInPlace(this.mRoomElement.Params.hillTopOffset);
            }
        }
        if (toffsetPoints.length === pBasePoly.length) {
            this.mHillPoints_Offset = toffsetPoints;
        }
        for (let tPoint of toffsetPoints) {
            tPointsOffset.push(new CSG.Vector2D(tPoint.x, tPoint.z));
        }
        if (toffsetPoints.length === pBasePoly.length) {
            tCAGOffset = CAG.fromPointsNoCheck(tPointsOffset);
        }
        if (tCAGOffset == null || (tCAGOffset.isSelfIntersecting())) {
            return (tRes);
        }
        let tCSGOffset = Util.CAG2CSG(tCAGOffset);
        tRes.CAG = tCAGOffset;
        tRes.CSG = tCSGOffset;
        return (tRes);
    }
    // tslint:disable-next-line:cyclomatic-complexity
    createCSG(pParent) {
        const tHillHeight = this.mRoomElement.Params.hillHeight;
        const tHillType = this.mRoomElement.Params.terrainType;
        let tbasePolygon = this.getCorrectedPolyPath();
        this.mHillPoints_Base = tbasePolygon;
        let tCAGCSGOffset = this.createOffsetCSG(tbasePolygon);
        if (tHillType === 'Top') {
            for (let tP1 of tbasePolygon) {
                tP1.addInPlace(this.mRoomElement.Params.hillTopOffset);
            }
        }
        // hill base plane / initial 2d shape
        let tPoints = new Array();
        for (let tPoint of tbasePolygon) {
            tPoints.push(new CSG.Vector2D(tPoint.x, tPoint.z));
        }
        let tCAGBase = CAG.fromPointsNoCheck(tPoints);
        if (tCAGBase.isSelfIntersecting()) {
            this.mRoomElement.setCAG(null);
            return (null);
        }
        this.mcanCreateHill = false;
        let tCSGBase = null;
        tCSGBase = Util.CAG2CSG(tCAGBase);
        this.mRoomElement.setCAG(tCAGBase);
        this.mRoomElement.setCAGoriginal(tCAGBase);
        if (tCAGCSGOffset.CAG != null && (!tCAGCSGOffset.CAG.isSelfIntersecting()) && (tHillType !== 'Flat')) {
            if (tCAGCSGOffset.CSG != null) {
                this.mcanCreateHill = true;
            }
            if (tHillType === "Top") {
                tCSGBase = tCSGBase.translate([0, tHillHeight, 0]);
                this.mRoomElement.setCAG(tCAGCSGOffset.CAG);
                this.mRoomElement.setCAGoriginal(tCAGCSGOffset.CAG);
            }
            if (tHillType === "Bottom" && tCAGCSGOffset.CSG != null) {
                tCAGCSGOffset.CSG = tCAGCSGOffset.CSG.translate([0, tHillHeight, 0]);
            }
        }
        let tMeshes_base = null;
        let tMeshes_offset = null;
        switch (tHillType) {
            case 'Flat':
                tMeshes_base = this.createMeshOfCSG(pParent, RoomManager.ROOM_SHAPE2D_PREFIX + 'spline', tCSGBase, this.mRoomElement.localMaterial);
                if (tMeshes_base == null) {
                    return (null);
                }
                return (tMeshes_base[0]);
            case 'Bottom':
                tMeshes_base = this.createMeshOfCSG(pParent, RoomManager.ROOM_SHAPE2D_PREFIX + 'spline', tCSGBase, this.mRoomElement.localMaterial);
                tMeshes_offset = this.createMeshOfCSG(pParent, RoomManager.ROOM_SHAPE2D_PREFIX + 'spline', tCAGCSGOffset.CSG, this.mRoomElement.localMaterial);
                if (tMeshes_base == null) {
                    return (null);
                }
                return (tMeshes_base[0]);
            case 'Top':
                tMeshes_base = this.createMeshOfCSG(pParent, RoomManager.ROOM_SHAPE2D_PREFIX + 'spline', tCSGBase, this.mRoomElement.localMaterial);
                tMeshes_offset = this.createMeshOfCSG(pParent, RoomManager.ROOM_SHAPE2D_PREFIX + 'spline', tCAGCSGOffset.CSG, this.mRoomElement.localMaterial);
                if (tMeshes_offset == null) {
                    return (null);
                }
                return (tMeshes_offset[0]);
            default:
                break;
        }
        return (null);
    }
    createSpline(pParent) {
        if (this.mPoints.length === 0)
            return (null);
        if (this.mClosedPolygon) {
            let tMesh = this.createCSG(pParent);
            if (tMesh == null) {
                this.drawOutline(pParent);
            }
            return (tMesh);
        }
        return (this.drawOutline(pParent));
    }
    // tslint:disable-next-line:cyclomatic-complexity
    createHill(pParent) {
        const basePoints = this.mHillPoints_Base;
        const offsetPoints = this.mHillPoints_Offset;
        const height = this.mRoomElement.Params.hillHeight;
        const splineType = this.mRoomElement.Params.terrainType;
        if (this.mHillPoints_Offset == null || this.mHillPoints_Base == null ||
            basePoints == null || offsetPoints == null || splineType === 'Flat' ||
            !this.mcanCreateHill) {
            return (null);
        }
        const canRibbon = offsetPoints.length === basePoints.length;
        if (!canRibbon) {
            newError('Unstable calculation', 'expansion failed');
            return (null);
        }
        let topPolygon = [];
        let bottomPolygon = [];
        if (splineType === 'Top') {
            topPolygon = this.mHillPoints_Base;
            bottomPolygon = this.mHillPoints_Offset;
        }
        if (splineType === 'Bottom') {
            topPolygon = this.mHillPoints_Offset;
            bottomPolygon = this.mHillPoints_Base;
        }
        const curve = this.curveFunction(this.mRoomElement.Params.hillProfile);
        const steps = getSteps(curve);
        const inters = steps.map((x, i) => getInterpolatedPolygon(bottomPolygon, topPolygon, x)
            .map((y) => { y.y += curve[i] * height; return y; }));
        const side = CreateRibbon('spline_hill_side', {
            pathArray: inters,
            closePath: true,
            sideOrientation: Mesh.BACKSIDE,
            updatable: true
        }, this.mScene);
        // side.convertToFlatShadedMesh();
        let positions = side.getVerticesData(VertexBuffer.PositionKind);
        if (positions != null) {
            let minX = Infinity;
            let minZ = Infinity;
            let maxX = -Infinity;
            let maxZ = -Infinity;
            let x = 0;
            let z = 0;
            let uvs = [];
            for (var i1 = 0; i1 < positions.length / 3; i1++) {
                x = positions[3 * i1];
                z = positions[3 * i1 + 2];
                minX = Math.min(x, minX);
                minZ = Math.min(z, minZ);
                maxX = Math.max(x, maxX);
                maxZ = Math.max(z, maxZ);
                uvs.push(x, z);
            }
            for (let i2 = 0; i2 < uvs.length / 2; i2++) {
                // uvs[2 * i2] = (uvs[2 * i2] - minX) / ((maxX - minX) * 0.15);
                // uvs[2 * i2 + 1] = (uvs[2 * i2 + 1] - minZ) / ((maxZ - minZ) * 0.15);
                uvs[2 * i2] = (uvs[2 * i2] - minX);
                uvs[2 * i2 + 1] = (uvs[2 * i2 + 1] - minZ);
            }
            side.updateVerticesData(VertexBuffer.UVKind, uvs);
        }
        side.parent = pParent;
        side.material = this.mRoomElement.localMaterial;
        side.receiveShadows = true;
        let bb = side.getBoundingInfo();
        this.mWidth = bb.maximum.x - bb.minimum.x;
        this.mDepth = bb.maximum.z - bb.minimum.z;
        this.mRoomElement.setWidth(this.mWidth, false);
        this.mRoomElement.setDepth(this.mDepth, false);
        this.mminPoint = new Vector3(bb.minimum.x, 0, bb.minimum.z);
        this.mmaxPoint = new Vector3(bb.maximum.x, this.mRoomElement.height, bb.maximum.z);
        return side;
    }
    createHelpLines(pParent) {
        if (this.mPoints.length === 0 || this.mPoints.length < 4)
            return;
        let tHelpLines = new Array();
        for (let i = 1; i < this.mPoints.length; i++) {
            tHelpLines.push([this.mPoints[0], this.mPoints[i]]);
        }
        let tLines = CreateLineSystem("shape_helplines", { lines: tHelpLines }, this.mScene);
        tLines.parent = pParent;
        tLines.renderingGroupId = 1;
        this.mRoomManager.app.model.layers.setMeshLayer(tLines, "room:ShapeHelpLines");
        tLines.color = Color3.FromHexString("#000000");
        ShadowGenerator.disableMeshShadow(tLines);
    }
}
function offset(polygon, spacing) {
    const geoInput = vectorCoordinates2JTS(polygon);
    if (!geoInput[0].equals(geoInput[geoInput.length - 1])) {
        geoInput.push(geoInput[0]);
    }
    const geometryFactory = new jsts.geom.GeometryFactory();
    const ring = geometryFactory.createLineString(geoInput);
    const shell = geometryFactory.createPolygon(ring, []);
    const bufferParameters = new jsts.operation.buffer.BufferParameters();
    bufferParameters.setEndCapStyle(jsts.operation.buffer.BufferParameters.CAP_FLAT);
    bufferParameters.setQuadrantSegments(1);
    bufferParameters.setJoinStyle(jsts.operation.buffer.BufferParameters.JOIN_MITRE);
    const buffered = shell.buffer(spacing, bufferParameters);
    const simplified = simplify();
    const points = simplified.getCoordinates();
    if (points.length === 0) {
        return [];
    }
    if (points[0].equals(points[points.length - 1])) {
        points.pop();
    }
    return points.map((x) => new Vector3(x.x, 0, x.y));
    function simplify() {
        let a;
        let b = buffered;
        let tol = 0.01;
        const targetVertexCount = polygon.length;
        let tNumPoint = b.getNumPoints();
        while (targetVertexCount + 1 < tNumPoint) {
            a = b;
            b = jsts.simplify.TopologyPreservingSimplifier.simplify(b, tol);
            tNumPoint = b.getNumPoints();
            if (a.getNumPoints() === tNumPoint) {
                tol *= 2;
                if (tol > 50) {
                    break;
                }
            }
        }
        return b;
    }
}
function vectorCoordinates2JTS(tPoints) {
    return tPoints.map((point) => new jsts.geom.Coordinate(point.x, point.z));
}
function getSteps(points) {
    const step = 1 / (points.length - 1);
    const res = [];
    for (let i = 0; i < points.length; i++) {
        res.push(i * step);
    }
    return res;
}
function getInterpolatedPolygon(a, b, n) {
    const res = [];
    for (let i = 0; i < a.length; i++) {
        res.push(b[i].subtract(a[i]).scale(n).add(a[i]));
    }
    return res;
}
